import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, setCsrfToken } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // False when the backend has no webmaster account yet — the login screen
  // uses this to point at setup.php instead of showing a dead form.
  const [installed, setInstalled] = useState(true);
  const [unreachable, setUnreachable] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await auth.me();
      setCsrfToken(data.csrf_token);
      setUser(data.user);
      setInstalled(data.installed !== false);
      setUnreachable(false);
      return data.user;
    } catch (err) {
      setUser(null);
      setUnreachable(err.code === 'network' || err.code === 'bad_response');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signIn = useCallback(async (email, password) => {
    const data = await auth.login(email, password);
    setCsrfToken(data.csrf_token);
    setUser(data.user);
    return data.user;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      setUser(null);
    }
  }, []);

  /** Used by the invite flow, which signs the new owner in server-side. */
  const adoptSession = useCallback((nextUser, csrf) => {
    setCsrfToken(csrf);
    setUser(nextUser);
  }, []);

  const value = {
    user,
    loading,
    installed,
    unreachable,
    signIn,
    signOut,
    refresh,
    adoptSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return context;
}

/** Where a given role belongs after signing in. */
export function homeForRole(role) {
  return role === 'webmaster' ? '/portal/webmaster' : '/portal/owner';
}
